<template>
    <div>
        <!-- Programme, Service and Client selection for staff/team leaders -->
        <template v-if="isAdminOrTeamLeader">
            <div class="selector-container">
                <p class="q-mb-sm text-bold">Programme or Service Select</p>
                <ProgrammeServiceSelector
                    v-model:selectedProgramme="selectedProgramme"
                    v-model:selectedService="selectedService"
                    :programme-options="sortedProgrammeList"
                    :service-options="sortedServiceList"
                    :loading-programmes="loadingProgrammes"
                    :loading-services="loadingServices"
                />
            </div>
            <div class="q-my-lg">
                <p class="q-mb-sm"><strong>Client Select</strong> &nbsp;(Optionally filter by programme or service above)</p>
                <q-select v-model="selectedClient" :options="displayedClientList"
                    option-label="full_name"
                    :loading="loadingClients" :placeholder="selectedClient ? '' : 'Name, NHI, email, phone, date of birth or ID'"
                    outlined dense clearable use-input input-debounce="300"
                    @filter="(val, update) => filterFn(val, update)"/>
            </div>
        </template>

        <!-- Direct client list for normal users -->
        <template v-else>
            <div class="form-row">
                <p class="q-mb-sm"><strong>Client Select</strong></p>
                <q-select v-model="selectedClient" :options="displayedClientList"
                    option-label="full_name" :loading="loadingClients" :placeholder="selectedClient ? '' : 'Name, NHI, email, phone, date of birth or ID'" outlined dense clearable use-input
                    input-debounce="300" @filter="(val, update) => filterFn(val, update)" />
            </div>
            <span class="text-body2 text-grey-8 text-center">The list of clients is based on who you are assigned to in
                Paua. If a client is missing, please contact innovation@embertechnology.nz.</span>
        </template>
    </div>
</template>

<script>
import api from "@/services/api/api"
import notify from '@/services/util/notify';
import token from "@/services/util/roleHandler";
import ProgrammeServiceSelector from "./ProgrammeServiceSelector.vue";
import { createPauaClientMatcher } from "@/services/util/pauaClientSearch";

export default {
    name: 'ClientSelector',
    components: { ProgrammeServiceSelector },
    props: {
        modelValue: {
            type: Object,
            default: null
        }
    },


    data() {
        return {
            selectedProgramme: null,
            selectedService: null,
            selectedClient: null,
            programmeList: [],
            displayedProgrammeList: [],
            programmeRawData: [],
            serviceList: [],
            displayedServiceList: [],
            serviceRawData: [],
            clientList: [],
            searchQuery: '',
            loadingProgrammes: false,
            loadingServices: false,
            loadingClients: false,
            role: token.getRole(),
            userInfo: token.getTokenFields(),
        }
    },

    
    async created() {
        if (this.isAdminOrTeamLeader) {
            await Promise.all([
                this.retrieveProgrammeList(),
                this.retrieveServiceList()
            ]);
        }
        await this.retrieveClientList();
    },


    computed: {
        isAdminOrTeamLeader() {
            // Adjust these role checks based on your actual role values
            return this.role === 'team_leader' || this.role === 'admin' || this.role === 'developer';
        },

        sortedProgrammeList() {
            return this.displayedProgrammeList.slice().sort();
        },

        sortedServiceList() {
            return this.displayedServiceList.slice().sort();
        },

        // Filter clients based on selected programme or service (for admin/team leader)
        filteredClientList() {
            if (!this.isAdminOrTeamLeader) {
                return this.clientList;
            }

            return this.clientList.filter(item => {
                if (this.selectedProgramme && this.selectedService) {
                    return item.programme_id == this.selectedProgramme || item.service_type_id == this.selectedService;
                }
                if (this.selectedProgramme) {
                    return item.programme_id == this.selectedProgramme;
                }
                if (this.selectedService) {
                    return item.service_type_id == this.selectedService;
                }
                return true;
            });
        },

        // Final displayed list with search applied
        displayedClientList() {
            if (!this.searchQuery) {
                return this.filteredClientList;
            }

            return this.filteredClientList.filter(createPauaClientMatcher(this.searchQuery));
        }
    },


    watch: {
        selectedClient(newValue) {
            this.$emit('update:modelValue', newValue);
        },

        selectedProgramme() {
            this.selectedClient = null; // Reset client selection when programme changes
        },

        selectedService() {
            this.selectedClient = null; // Reset client selection when service changes
        },

        modelValue: {
            immediate: true,
            handler(newValue) {
                this.selectedClient = newValue;
            }
        }
    },


    methods: {
        // Filter programmes by user role and assigned sources
        filterProgrammeList() {
            this.displayedProgrammeList = [];
            if (this.role != 'admin' && this.role != 'developer') {
                const sources = this.userInfo.sources ?? [this.userInfo.source];
                this.displayedProgrammeList = this.displayedProgrammeList.concat(
                    ...sources.map(source => this.programmeRawData[0][source] || [])
                );
            } else if (this.role == 'admin' || this.role == 'developer') {
                this.displayedProgrammeList = this.programmeList;
            }
        },
        

        // Retrieve the programme list based on user role
        async retrieveProgrammeList() {
            this.loadingProgrammes = true;
            try {
                const res = await api.client.getProgrammeList();
                this.programmeRawData = res.data['programmes'];
                for (const values of Object.values(this.programmeRawData[0])) {
                    this.programmeList.push(...values);
                }
                this.filterProgrammeList();
            } catch (error) {
                console.log(error)
                notify.error("Failed to retrieve form programmes list", "top");
            } finally {
                this.loadingProgrammes = false;
            }
        },

        // Retrieve the service list
        async retrieveServiceList() {
            this.loadingServices = true;
            try {
                const res = await api.client.getServicesList();
                this.serviceRawData = res.data['services'];
                this.serviceList = this.serviceRawData;
                this.displayedServiceList = this.serviceList;
            } catch (error) {
                console.log(error)
                notify.error("Failed to retrieve services list", "top");
            } finally {
                this.loadingServices = false;
            }
        },


        // Retrieve the client list based on user role
        async retrieveClientList() {
            this.loadingClients = true;
            try {
                const res = await api.client.getClientList();
                this.clientList = res.data;
            } catch (error) {
                notify.error("Failed to retrieve form client list", "top");
            } finally {
                this.loadingClients = false;
            }
        },

        filterFn(inputVal, update) {
            update(() => {
                this.searchQuery = inputVal;
            });
        },
    }
}
</script>
